import React, { useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import moment from 'moment'
import { flatten, isNil, noop } from 'lodash-es'
import {
  Currency,
  DealActions,
  DealDetail,
  DealStatus,
  NegotiationFieldMeta,
  RepoType,
  Severity,
  UserPreference,
  ValidationIssue
} from 'api'
import { ModalFooter, ModalWindow, Table } from 'view/components/panels'
import { DialogField, DialogRow } from 'view/components/inputs'
import { TradingDatePickerField } from '../../entity'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { createInvalidValueValidationIssue, createMissingMandatoryValidationIssue, getDateEditorFormat } from 'model'
import { DateLike, Decimal, notNilFilter, toLocalDateString } from 'utils'
import { createFilteredDealDetailsSelector } from 'selectors/dealSelectors'
import { platformTradeDateSelector } from 'selectors/platformSelectors'
import { createUserPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { Locale, Translation } from 'locale'
import { ValidationIssuesContainer } from '../../error'
import {
  canServicingAccept,
  canServicingCancel,
  canServicingRevertAttributes,
  canServicingSend,
  getNegotiatedValue
} from 'view/routes/deals/components/utils'
import { Content, FieldsContainer } from '../servicingDialogStyles'
import { isStockBorrowingLendingEnabledSelector } from 'selectors/featureSelectors'
import { ModalConsumerProps, NotificationType, withModal } from '../../modal'
import { useOnMount } from 'view/hooks'
import { useDealDetailsEffectiveDateInfo } from '../useGetEffectiveDateInfo'
import { BulkRerateInputs } from '../BulkRerateInputs'
import { rerateOptions, useServicingRerateColumns } from './ServicingRerateGridModel'

export type ServicingRerateDialogContainerProps = OwnProps & StateProps & DispatchProps

interface OwnProps {
  dealIds: string[]
  readOnly?: { [P in NegotiationFieldMeta]?: boolean }
  onClose?: () => unknown
}

interface StateProps {
  dealDetails: DealDetail[]
  platformTradeDate: DateLike | null
  userPreference: UserPreference
  stockBorrowingLendingEnabled: boolean
}

interface DispatchProps {
  onServicingStartRerateBulk: (dealIds: string[]) => void
  onUpdateField: typeof DealActions.servicingUpdateAttributes
  onRevert: typeof DealActions.servicingRevertAttributes
  onSend: (dealId: string) => unknown
  onCancel: typeof DealActions.servicingCancel
}

export interface ServicingRerateDialogComponentProps extends Omit<ServicingRerateDialogContainerProps, 'onSend'> {
  titleLabel: Translation
  canSend: (dealDetail: DealDetail) => boolean
  confirmBtnLabel: Translation
  onSend: (dealIds: string[]) => Promise<void>
}

export const ServicingRerateDialogComponent: React.FC<ServicingRerateDialogComponentProps> = props => {
  const {
    canSend,
    titleLabel,
    confirmBtnLabel,
    dealDetails,
    platformTradeDate,
    userPreference,
    onServicingStartRerateBulk,
    onClose = noop,
    onUpdateField,
    onSend,
    onCancel
  } = props
  const firstDealDetail = dealDetails[0] ? dealDetails[0] : null

  if (dealDetails.some(dealDetail => dealDetail.deal.currency !== firstDealDetail?.deal.currency)) {
    throw new Error('Bulk rerate only applies to deals with the same currency')
  }

  const { defaultEffectiveDate, minEffectiveDate, maxEffectiveDate, isBeforeCutOffTime } = useDealDetailsEffectiveDateInfo(dealDetails)

  const title = `${Locale.translate(titleLabel)} (${dealDetails.length})`

  const dateEditorFormat = getDateEditorFormat(userPreference.dateFormat)

  const dealEffectiveDates = dealDetails.map(dealDetail =>
    getNegotiatedValue(dealDetail.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.RERATE_DATE]?.negotiableFieldRequired)
  )
  const firstDealEffectiveDate = dealEffectiveDates[0]

  // We should be able to assume the first deal's rerate date is valid as we update them all in the useEffect below if they don't all match
  const effectiveDate = !dealEffectiveDates.some(dealEffectiveDate => firstDealEffectiveDate !== dealEffectiveDate)
    ? firstDealEffectiveDate
    : null

  const validationIssues: ValidationIssue[] = [
    isNil(effectiveDate) ? createMissingMandatoryValidationIssue('repoInterestStartDate') : null,
    isNil(platformTradeDate) || isNil(effectiveDate)
      ? null
      : moment(effectiveDate).isSame(platformTradeDate, 'day') && !isBeforeCutOffTime
      ? createInvalidValueValidationIssue(
          'repoInterestStartDate',
          Locale.translate(Translation.SC_EFFECTIVE_DATE_TODAY_WARNING_MESSAGE),
          Severity.WARN
        )
      : null
  ]
    .concat(flatten(dealDetails.map(dealDetail => dealDetail.servicingSideDetails?.validationIssues ?? [])))
    .filter(notNilFilter)

  const hasValidationIssues = validationIssues.filter(validationIssue => validationIssue.severity === Severity.ERROR).length > 0

  const handleRerate = (adjusment: number, isBips: boolean) => {
    dealDetails.forEach(dealDetail => {
      const isStockBorrowingLending = dealDetail.deal.repoType === RepoType.STOCK_BORROWING_LENDING
      if (isStockBorrowingLending) {
        handleUpdateField({
          dealId: dealDetail.deal.dealId,
          value: isBips ? Decimal.add(dealDetail.deal.fee!!, adjusment) : adjusment,
          field: NegotiationFieldMeta.FEE
        })
      } else {
        handleUpdateField({
          dealId: dealDetail.deal.dealId,
          value: isBips ? Decimal.add(dealDetail.deal.rate!!, adjusment) : adjusment,
          field: NegotiationFieldMeta.RATE
        })
      }
    })
  }

  const handleUpdateField = useCallback(
    ({ dealId, value, field, rawValue }: { dealId: string; value: any; field: NegotiationFieldMeta; rawValue?: any }) => {
      onUpdateField({
        dealId,
        field,
        updatedValue: value,
        updatedRawValue: rawValue,
        decisionMakerOrientedByNegotiationSide: true,
        meta: { haircutSign: userPreference.haircutSign },
        revertFirst: false
      })
    },
    [onUpdateField, userPreference.haircutSign]
  )

  const onSetEffectiveDate = useCallback(
    (newEffectiveDate: DateLike | null) => {
      dealDetails.forEach(dealDetail => {
        handleUpdateField({ dealId: dealDetail.deal.dealId, value: newEffectiveDate, field: NegotiationFieldMeta.RERATE_DATE })
      })
    },
    [dealDetails, handleUpdateField]
  )

  const columns = useServicingRerateColumns(handleUpdateField)

  const handleSend = async () => {
    await onSend(dealDetails.map(dealDetail => dealDetail.deal.dealId))
    onClose()
  }

  const handleCancel = async () => {
    dealDetails.forEach(dealDetail => {
      if (canServicingCancel(dealDetail)) {
        onCancel(dealDetail.deal.dealId)
      }
    })
    onClose()
  }

  useOnMount(() => {
    const dealIds = dealDetails
      .filter(dealDetail => dealDetail.deal.status !== DealStatus.SERVICING_WITH_INITIATOR)
      .map(dealDetail => dealDetail.deal.dealId)

    if (dealIds.length > 0) {
      onServicingStartRerateBulk(dealIds)
    }
  })

  const [effectiveDatesAdjusted, setEffectiveDatesAdjusted] = useState<boolean>(false)

  useEffect(() => {
    if (dealDetails.some(dealDetail => !dealDetail.servicingSideDetails)) {
      return
    }

    if (dealEffectiveDates.every(dealEffectiveDate => firstDealEffectiveDate === dealEffectiveDate)) {
      setEffectiveDatesAdjusted(true)
      return
    }

    if (dealEffectiveDates.some(dealEffectiveDate => firstDealEffectiveDate !== dealEffectiveDate) && !effectiveDatesAdjusted) {
      onSetEffectiveDate(toLocalDateString(defaultEffectiveDate))
      setEffectiveDatesAdjusted(true)
    }
  }, [dealDetails, dealEffectiveDates, defaultEffectiveDate, effectiveDatesAdjusted, firstDealEffectiveDate, onSetEffectiveDate])

  return (
    <ModalWindow title={title} onEscKey={handleCancel}>
      <Content size="medium">
        <FieldsContainer>
          {!isBeforeCutOffTime && (
            <ValidationIssuesContainer>
              {<li>{Locale.translate(Translation.SC_REQUEST_INITIATED_AFTER_CUT_OFF_TIME)}</li>}
            </ValidationIssuesContainer>
          )}
          <BulkRerateInputs handleRerate={handleRerate} />
          <DialogRow>
            <DialogField flex={1}>
              <TradingDatePickerField
                label={Locale.translate(Translation.SC_EFFECTIVE_DATE)}
                fieldName="repoInterestStartDate"
                currency={firstDealDetail?.deal.currency ?? Currency.EUR}
                minDate={minEffectiveDate ?? undefined}
                maxDate={maxEffectiveDate ?? undefined}
                dateFormat={dateEditorFormat}
                validationIssues={validationIssues}
                value={effectiveDate}
                onUpdate={(value: DateLike | null) => {
                  onSetEffectiveDate(value)
                }}
                dataTestId={'ServicingRerateDialog-EffectiveDateTradingDatePickerField'}
              />
            </DialogField>
            <DialogField flex={2} />
          </DialogRow>
        </FieldsContainer>
        <Table columns={columns} options={rerateOptions} rows={dealDetails} />
      </Content>
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton type={TextButtonType.Tertiary} onClick={handleCancel} dataTestId={'ServicingRerateDialog-CancelButton'}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton
              type={TextButtonType.Primary}
              disabled={hasValidationIssues || !dealDetails.some(canServicingRevertAttributes) || dealDetails.some(d => !canSend(d))}
              onClick={handleSend}
              dataTestId={'ServicingRerateDialog-SendRequestButton'}>
              {Locale.translate(confirmBtnLabel)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}

const dealIdsSelector = (_: {}, props: ServicingRerateDialogComponentProps) => props.dealIds

const mapStateToProps = createStructuredSelector({
  dealDetails: createFilteredDealDetailsSelector(dealIdsSelector),
  platformTradeDate: platformTradeDateSelector,
  userPreference: createUserPreferenceSelector(),
  stockBorrowingLendingEnabled: isStockBorrowingLendingEnabledSelector
})

const mapDispatchToProps = {
  onUpdateField: DealActions.servicingUpdateAttributes,
  onRevert: DealActions.servicingRevertAttributes,
  onCancel: DealActions.servicingCancel
}

export const ServicingRerateDialog = connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, {
  ...mapDispatchToProps,
  onSend: DealActions.servicingSend,
  onServicingStartRerateBulk: (dealIds: string[]) =>
    DealActions.servicingStartRerateBulk({
      dealServicingStartRerateAttributesList: dealIds.map(dealId => ({ dealId, isCloseOpen: false }))
    })
})((props: ServicingRerateDialogContainerProps) => {
  const { onSend, onClose = noop } = props
  return (
    <ServicingRerateDialogComponent
      {...props}
      titleLabel={Translation.TC_RERATE}
      canSend={canServicingSend}
      confirmBtnLabel={Translation.TC_BUTTON_SEND_REQUEST}
      onSend={async dealIds => {
        dealIds.forEach(id => onSend(id))
        onClose()
      }}
    />
  )
})

export const ServicingAdminRerateDialog = connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, {
  ...mapDispatchToProps,
  onSend: DealActions.servicingAccept,
  onServicingStartRerateBulk: (dealIds: string[]) => DealActions.servicingStartAdminRerateBulk({ dealIds })
})(
  withModal((props: ServicingRerateDialogContainerProps & ModalConsumerProps) => {
    const { showDialog, onClose = noop } = props
    return (
      <ServicingRerateDialogComponent
        {...props}
        canSend={canServicingAccept}
        titleLabel={Translation.TC_RERATE}
        confirmBtnLabel={Translation.TC_RERATE}
        onSend={async dealIds => {
          if (
            (await showDialog({
              buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
              type: NotificationType.Confirm,
              title: Translation.TC_RERATE_TRADE,
              messageText: Locale.translate(Translation.SC_RERATE_CONFIRM)
            })) === Translation.TC_BUTTON_YES
          ) {
            dealIds.forEach(id => props.onSend(id))
            onClose()
          }
        }}
      />
    )
  })
)
